import { Liveness } from '@/common/types';
import { config } from '@/config';
import { useKycSessionStore } from '@/stores/kycSession.store';
import { getIdvVendor } from '@/util/idvVendorUtil';
import type { RouteLocationNormalized } from 'vue-router';

interface CustomEventProperties extends Record<string, string | number | boolean | undefined> {
  liveness: Liveness;
  idv_provider?: string;
  region?: string;
}
export default class MixpanelEvent {
  static readonly defaultProperties = {
    app: 'fluorite',
    type: 'Kyc',
    liveness: config.testModeEnabled ? Liveness.TEST : Liveness.LIVE,
    origin: document.location.origin,
  };

  constructor(
    readonly name: string,
    readonly properties: CustomEventProperties
  ) {}

  static get commonKycProperties() {
    const kycSessionStore = useKycSessionStore();
    const kycProvider = kycSessionStore.kycSession?.kycProvider;

    return {
      kycSessionStore,
      region: kycSessionStore.kycSession?.region,

      kycProvider,
      kycProviderSdkVersion:
        kycProvider === 'sumsub' ? undefined : kycSessionStore.kycSession?.oneSdkVersion,
    };
  }

  static createPageViewEvent(location: RouteLocationNormalized) {
    const { kycSessionStore, region, kycProvider, kycProviderSdkVersion } =
      this.commonKycProperties;
    const url = document.location.origin + document.location.pathname;

    return new MixpanelEvent('PageView', {
      ...this.defaultProperties,
      page_title: location.name?.toString(),
      url,
      $current_url: url,
      path: location.path,
      route: [...location.matched].reverse()[0]?.path,
      region,
      idv_provider: kycProvider === 'sumsub' ? 'sumsub' : getIdvVendor(region),
      partner_account_id: kycSessionStore.kycSession?.partnerAccountId,
      cardholder_account_id: kycSessionStore.kycSession?.cardholder.accountId,
      kyc_provider_sdk_version: kycProviderSdkVersion,
    });
  }

  static createInteractionEvent(
    action:
      | 'AreaCodeChanged'
      | 'EmailEntered'
      | 'PhoneNumberEntered'
      | 'ConsentGiven'
      | 'AddressValidationSucceeded'
      | 'AddressValidationFailed'
      | 'VerifiableAddressEntered'
      | 'UnverifiableAddressEntered'
      | 'ManualAddressFormOpened'
      | 'ManualAddressEntered'
      | 'ManualAddressSubmitted'
      | 'AddressSearched'
      | 'CountrySelected'
      | 'SpendSpecified',
    interactionType?: string, // 'option_selected' | 'button_clicked' | 'input_value_updated' | 'link_clicked'
    value?: string | number | boolean
  ) {
    return new MixpanelEvent('UserInteraction', {
      ...this.defaultProperties,
      action,
      interaction_type: interactionType,
      value,
    });
  }

  static createIdvStepsCompletedEvent(
    action:
      | 'DocumentUploadStarted'
      | 'DocumentFrontSideUploaded'
      | 'DocumentBackSideUploaded'
      | 'BiometricsStarted'
      | 'BiometricsCompleted'
  ) {
    const { kycSessionStore, region, kycProvider, kycProviderSdkVersion } =
      this.commonKycProperties;
    const idvProvider = kycProvider === 'sumsub' ? 'sumsub' : getIdvVendor(region);
    const onfidoSdkVersion =
      idvProvider === 'onfido' ? kycSessionStore.kycSession?.onfidoSdkVersion : undefined;

    return new MixpanelEvent('IdvStepsCompleted', {
      ...this.defaultProperties,
      action,
      idv_provider: idvProvider,
      interactionType: 'idv_flow',
      region,
      kyc_provider_sdk_version: kycProviderSdkVersion,
      onfido_sdk_version: onfidoSdkVersion,
    });
  }
}
